import * as yup from 'yup';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import api from "../../api/axiosInstance";
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';

const SENIORITY_LEVELS = [
  'Entry Level (0-1 years)',
  'Junior level (1-2 years)',
  'Mid Level (3-5 years)',
  'Senior level (6+ years)'
];

const PRIMARY_ROLES = [
  'Mobile Developer',
  'Full-stack Developer',
  'Front-end Developer',
  'Back-end Developer',
  'Business Analyst',
  'UI/UX Designer',
  'Software Tester/QA Engineer',
  'QA Lead/Test Manager',
  'Project Manager',
  'Enterprise Architect',
  'Software Architect',
  'Scrum Master',
  'DevOps Engineer',
  'Team Lead/Tech Lead',
  'DBA/DB Developer',
  'Other'
];

const FORM_DEFAULTS = {
  seniority: '',
  primaryRoles: [],
  otherPrimaryRole: ''
};

const FORM_STORAGE_KEY = 'experienceForm';

const experienceSchema = yup.object().shape({
  seniority: yup.string().required('Seniority level is required'),
  primaryRoles: yup
    .array()
    .length(1, 'Please select your primary role')
    .required('Primary role is required'),
  otherPrimaryRole: yup
    .string()
    .when('primaryRoles', {
      is: (primaryRoles) => primaryRoles?.includes('Other'),
      then: (schema) => schema.required('Please specify your primary role'),
      otherwise: (schema) => schema.notRequired()
    })
});

const storageUtils = {
  saveFormData: (data) => {
    try {
      localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(data));
    } catch (error) {}
  },
  
  loadFormData: () => {
    try {
      const saved = localStorage.getItem(FORM_STORAGE_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      return null;
    }
  },
  
  clearFormData: () => {
    try {
      localStorage.removeItem(FORM_STORAGE_KEY);
    } catch (error) {}
  }
};

const useExperienceForm = (formData) => {
  const savedData = storageUtils.loadFormData();
  const initialData = {
    ...FORM_DEFAULTS,
    ...formData,
    ...savedData 
  };

  return useForm({
    resolver: yupResolver(experienceSchema),
    mode: 'onChange',
    defaultValues: initialData
  });
};

const apiService = {
  saveExperience: async (data) => {
    const serverUrl = process.env.REACT_APP_SERVER_URL;

    if (!serverUrl) {
      throw new Error('API configuration is missing. Please contact support.');
    }

    const body = {
      seniority: data.seniority || '',
      primaryRole: data.primaryRoles?.[0] || '',
      roles: data.primaryRoles || [],
      otherPrimaryRole: data.otherPrimaryRole || '',
    };

    try {
      const response = await api.patch(
        "/users/me",
        body,
        { headers: { "Content-Type": "application/json",} }
      );
      return response.data;
    } catch (error) {
      const profileResponse = await api.get("/users/me");
      const userId = profileResponse.data._id;

      if (!userId) throw new Error('User ID not found. Please try logging in again.');

      try {
        const candidateCheck = await api.get(`/candidate/${userId}`);
      } catch (checkError) {
        if (checkError.response?.status === 404) {
          throw new Error('Candidate profile not found. Please complete your profile setup first.');
        }
      }

      const candidateResponse = await api.patch(
        `/candidate/${userId}`,
        body,
        { headers: { "Content-Type": "application/json",} }
      );
      return candidateResponse.data;
    }
  }
};

const FormHeader = () => (
  <Box sx={{ textAlign: 'center', mb: 4 }}>
    <Typography 
      variant="h3" 
      sx={{ 
        fontFamily: 'Readex Pro, sans-serif',
        fontWeight: 500,
        fontSize: '20px',
        lineHeight: '30px',
        letterSpacing: '0%',
        textAlign: 'center',
        color: '#000000',
        mb: 1
      }}
    >
      Experience
    </Typography>
    <Typography 
      variant="body1" 
      sx={{ 
        fontFamily: 'Inter, sans-serif',
        fontWeight: 300,
        fontSize: '16px',
        lineHeight: '24px',
        letterSpacing: '0%',
        textAlign: 'center',
        color: '#000000'
      }}
    >
      What do you specialize in?
    </Typography>
  </Box>
);

const SeniorityField = ({ control, errors }) => (
  <Controller
    name="seniority"
    control={control}
    render={({ field }) => (
      <FormControl fullWidth variant="outlined" size="small" error={!!errors.seniority}>
        <InputLabel sx={{ fontSize: '14px' }}>Seniority</InputLabel>
        <Select
          {...field}
          label="Seniority"
          sx={{
            borderRadius: 1,
            fontSize: '14px',
            '& fieldset': {
              borderColor: '#d0d0d0',
            },
            '&:hover fieldset': {
              borderColor: '#999',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#FF5722',
            },
          }}
        >
          <MenuItem value="">
            <em>Select</em>
          </MenuItem>
          {SENIORITY_LEVELS.map((level) => (
            <MenuItem key={level} value={level}>
              {level}
            </MenuItem>
          ))}
        </Select>
        {errors.seniority && (
          <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5 }}>
            {errors.seniority.message}
          </Typography>
        )}
      </FormControl>
    )}
  />
);

const PrimaryRolesField = ({ control, errors, onRoleToggle, selectedRoles }) => (
  <Controller
    name="primaryRoles"
    control={control}
    render={({ field }) => (
      <Box>
        <Typography 
          variant="body1" 
          sx={{ 
            fontSize: '14px',
            fontWeight: 500,
            color: '#333',
            mb: 2
          }}
        >
          Primary Role *
        </Typography>
        
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 1.5,
          mb: 1
        }}>
          {PRIMARY_ROLES.map((role) => {
            const isSelected = selectedRoles?.includes(role);
            
            return (
              <Chip
                key={role}
                label={role}
                clickable
                onClick={() => onRoleToggle(role)}
                variant={isSelected ? 'filled' : 'outlined'}
                sx={{
                  borderRadius: '16px',
                  fontSize: '14px',
                  fontWeight: 400,
                  height: '32px',
                  backgroundColor: isSelected ? '#FF5722' : 'transparent',
                  color: isSelected ? 'white' : '#333',
                  borderColor: isSelected ? '#FF5722' : '#d0d0d0',
                  '&:hover': {
                    backgroundColor: isSelected ? '#E64A19' : '#f5f5f5',
                    borderColor: '#FF5722'
                  },
                  '&.MuiChip-clickable:hover': {
                    backgroundColor: isSelected ? '#E64A19' : '#f5f5f5'
                  }
                }}
              />
            );
          })}
        </Box>
        
        {errors.primaryRoles && (
          <Typography variant="caption" color="error" sx={{ ml: 0, mt: 0.5 }}>
            {errors.primaryRoles.message}
          </Typography>
        )}
      </Box>
    )}
  />
);

const OtherRoleField = ({ control, errors, isVisible }) => {
  if (!isVisible) return null;

  return (
    <Controller
      name="otherPrimaryRole"
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          fullWidth
          label="Other Primary Role"
          placeholder="Enter Primary Role"
          variant="outlined"
          size="small"
          error={!!errors.otherPrimaryRole}
          helperText={errors.otherPrimaryRole?.message}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 1,
              fontSize: '14px',
              '& fieldset': {
                borderColor: '#d0d0d0',
              },
              '&:hover fieldset': {
                borderColor: '#999',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#FF5722',
              },
            },
            '& .MuiInputLabel-root': {
              fontSize: '14px',
            }
          }}
        />
      )}
    />
  );
};

const FormActions = ({ 
  onBack, 
  isValid, 
  isSubmitting 
}) => (
  <Box sx={{ 
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'center',
    pt: 0.5,
  }}>
    <Button
      variant="outlined"
      startIcon={<ArrowBackIcon />}
      onClick={onBack}
      disabled={isSubmitting}
      size="small"
      sx={{
        color: '#666',
        borderColor: '#d0d0d0',
        px: 3,
        py: 1,
        fontWeight: 'bold',
        fontSize: '12px',
        borderRadius: 1,
        textTransform: 'uppercase',
        '&:hover': {
          borderColor: '#999',
          backgroundColor: '#f8f8f8'
        },
        '&:disabled': {
          opacity: 0.4
        }
      }}
    >
      BACK
    </Button>
    
    <Box />

    <Button
      type="submit"
      variant="contained"
      disabled={!isValid || isSubmitting}
      size="small"
      sx={{
        backgroundColor: '#FF5722',
        color: 'white',
        px: 3,
        py: 1,
        fontWeight: 'bold',
        fontSize: '12px',
        borderRadius: 1,
        textTransform: 'uppercase',
        boxShadow: '0 2px 8px rgba(255, 87, 34, 0.3)',
        '&:hover': {
          backgroundColor: '#E64A19',
          boxShadow: '0 4px 12px rgba(255, 87, 34, 0.4)',
          transform: 'translateY(-1px)'
        },
        '&:disabled': {
          backgroundColor: '#ccc',
          boxShadow: 'none'
        }
      }}
    >
      {isSubmitting ? (
        <>
          <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
          SAVING...
        </>
      ) : (
        'NEXT'
      )}
    </Button>
  </Box>
);

const StatusAlert = ({ saveStatus, onClose }) => {
  if (!saveStatus) return null;

  return (
    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
      <Alert 
        severity={saveStatus.type} 
        sx={{ width: '512px' }}
        onClose={onClose}
        action={
          saveStatus.type === 'error' && saveStatus.retry ? (
            <Button color="inherit" size="small" onClick={saveStatus.retry}>
              RETRY
            </Button>
          ) : null
        }
      >
        {saveStatus.message}
      </Alert>
    </Box>
  );
};

const Experience = ({ 
  formData, 
  setFormData, 
  onNext, 
  onBack
}) => {
  const navigate = useNavigate();
  const [saveStatus, setSaveStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const form = useExperienceForm(formData);
  const { control, handleSubmit, setValue, watch, trigger, formState: { errors, isValid } } = form;

  useEffect(() => {
    const subscription = watch((watchedValues) => {
      const cleanedValues = Object.keys(watchedValues).reduce((acc, key) => {
        if (watchedValues[key] !== undefined) {
          acc[key] = watchedValues[key];
        }
        return acc;
      }, {});

      storageUtils.saveFormData(cleanedValues);

      setFormData(prev => ({ ...prev, ...cleanedValues }));
    });
    
    return () => subscription.unsubscribe();
  }, [watch, setFormData]);

  const handleRoleToggle = useCallback((role) => {
    const currentRoles = watch('primaryRoles') || [];
    const isSelected = currentRoles.includes(role);
    
    if (isSelected) {
      setValue('primaryRoles', []);
    } else {
      setValue('primaryRoles', [role]);
    }
    
    trigger('primaryRoles');
  }, [watch, setValue, trigger]);

  const onSubmit = useCallback(async (data) => {
    setIsSubmitting(true);
    setSaveStatus(null);
    
    try {
      await apiService.saveExperience(data);
      setFormData(prev => ({ ...prev, ...data }));
      storageUtils.clearFormData();
      onNext();
    } catch (error) {
      setSaveStatus({ 
        type: 'error', 
        message: error.message || 'Failed to save experience details. Please try again.',
        retry: () => {
          setSaveStatus(null);
          handleSubmit(onSubmit)();
        }
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [setFormData, onNext, handleSubmit]);

  const selectedRoles = watch('primaryRoles');
  const isOtherSelected = useMemo(() => selectedRoles?.includes('Other'), [selectedRoles]);

  return (
    <Box sx={{ 
      height: 'calc(100vh - 116px)',
      backgroundColor: '#DED8D1',
      pt: 2,
      pb: 0,
      px: '80px',
      overflow: 'hidden'
    }}>
      <Container 
        maxWidth={false}
        sx={{ 
          px: 0,
          width: '100%',
          height: '100%',
          overflow: 'hidden'
        }}
      >
        <Box 
          sx={{ 
            p: { xs: 2, sm: 3, md: 4 }, 
            backgroundColor: 'white',
            borderRadius: 0,
            boxShadow: 'none',
            border: 'none',
            margin: '0 auto',
            mx: 0,
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <FormHeader />
          <StatusAlert 
            saveStatus={saveStatus} 
            onClose={() => setSaveStatus(null)} 
          />

          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%'
          }}>
            <Box sx={{
              width: '512px',
              display: 'flex',
              flexDirection: 'column',
              gap: '24px'
            }}>
              <form 
                onSubmit={handleSubmit(onSubmit)} 
                style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '24px' 
                }}
              >
                <SeniorityField 
                  control={control} 
                  errors={errors} 
                />

                <PrimaryRolesField
                  control={control}
                  errors={errors}
                  onRoleToggle={handleRoleToggle}
                  selectedRoles={selectedRoles}
                />

                <OtherRoleField
                  control={control}
                  errors={errors}
                  isVisible={isOtherSelected}
                />

                <Box sx={{ height: '12px' }} />

                <FormActions
                  onBack={onBack}
                  isValid={isValid}
                  isSubmitting={isSubmitting}
                />
              </form>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Experience;